import type { ManagedSource, SourceRoleContract } from "./types";


export const SOURCE_INLINE_LIMIT = 5;

export type SourceAnalysisState = "absent" | "importing" | "pending" | "running" | "succeeded" | "invalid";

export type SourceCategoryStats = {
  fileCount: number;
  totalBytes: number;
  readyCount: number;
  invalidCount: number;
  pendingCount: number;
  analysis: SourceAnalysisState;
  analysisProgress: string | null;
};

export function categoryStats(
  role: SourceRoleContract,
  sources: ManagedSource[],
  analysis: SourceAnalysisState | undefined,
  invalidSourceIds: ReadonlySet<string>,
  analysisProgress: string | null = null,
): SourceCategoryStats {
  const invalidSources = sources.filter((source) => {
    return source.status === "failed" || invalidSourceIds.has(source.id);
  });
  const invalidIds = new Set(invalidSources.map((source) => source.id));
  const invalidCount = invalidSources.length;
  let analysisState: SourceAnalysisState = analysis ?? "absent";
  if (role.analysisRequired && sources.length && analysisState === "absent") {
    analysisState = "pending";
  }
  let readyCount = sources.filter((source) => source.status === "ready" && !invalidIds.has(source.id)).length;
  let pendingCount = sources.length - readyCount - invalidCount;
  if (role.analysisRequired && !["succeeded", "invalid"].includes(analysisState)) {
    readyCount = 0;
    pendingCount = sources.length - invalidCount;
  }
  return {
    fileCount: sources.length,
    totalBytes: sources.reduce((total, source) => total + Number(source.sizeBytes), 0),
    readyCount,
    invalidCount,
    pendingCount,
    analysis: analysisState,
    analysisProgress,
  };
}

export function sourceExtension(source: ManagedSource) {
  const name = source.virtualPath || source.originalFilename;
  const position = name.lastIndexOf(".");
  if (position < 0) return "";
  return name.slice(position).toLowerCase();
}

export function sourceName(source: ManagedSource) {
  return source.virtualPath || source.originalFilename;
}

export function formatSourceBytes(value: number) {
  if (!Number.isFinite(value)) return "—";
  if (value < 1024) return `${value} o`;
  return `${(value / 1024 / 1024).toFixed(2)} Mo`;
}

export function formatSourceDate(value: string) {
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}
